import restClient from 'restClient'
import { v4 as uuidv4 } from 'uuid'

const API_URL = window.API_ROOT + '/api'
const restClientInstance = restClient(API_URL)

export interface AdaStepConfig {
  key: string
  description: string
  result_url?: string
}

export interface AdaRegistrationRequest {
  session_id: string
  name: string
  company_email?: string
  country_iso2: string
  company_website: string
  data_source?: string
  data_processor?: string
}

export interface AdaStepResult {
  key: string
  status: string
  data: Record<string, any>
  errors?: Array<{ code: string; details: string }>
  warnings?: Array<{ code: string; details: string }>
}

export interface AdaInitializeResponse {
  status: string
  session_id: string
  run_all_steps_url?: string
  steps?: AdaStepConfig[]
  errors?: Array<{ code: string; details: string }>
}

export interface AdaRunAllStepsResponse {
  status: string
  session_id: string
  step_statuses: Record<string, string>
  result_urls: Record<string, string>
  steps_url: string
  errors?: Array<{ code: string; details: string }>
}

export interface AdaRegistrationResponse {
  status: string
  session_id: string
  name?: string
  company_email?: string
  country_iso2?: string
  company_website?: string
  data_source?: string
  data_processor?: string
  step_statuses: Record<string, string>
  result_urls: Record<string, string>
  data: AdaStepResult[]
  errors: Array<{ code: string; details: string }>
  warnings: Array<{ code: string; details: string }>
}

interface AsyncPollingInProgressResponse {
  success: boolean
  status: 'processing'
  result_url: string
  session_id?: string
}

type AdaRegistrationCallback = (response: AdaRegistrationResponse | null, error: Error | null) => void

type StepStatusCallback = (stepStatuses: Record<string, string>) => void

type StepConfigsCallback = (stepConfigs: AdaStepConfig[]) => void

const DEFAULT_STEP_KEYS = [
  'business_details',
  'about_us',
  'why_choose_us',
  'location_details',
  'service_offering',
  'customer_reviews',
  'social_media',
] as const

/**
 * Service for managing Ada-assisted registration flow.
 *
 * Polling Strategy:
 * - Frontend polls indefinitely until backend responds or user cancels/skips
 * - Backend enforces a 3-minute timeout for AI processing (steps_service.py)
 * - If AI takes longer than 3 minutes, backend marks remaining steps as TIMEOUT errors
 * - Users can skip the loading screen after 10 seconds via UI
 * - Data is persisted in AdaRegistrationLog, so users can review later even if they skip
 */
class AdaRegistrationService {
  private sessionId: string | null = null
  private pendingRequest: Promise<AdaRegistrationResponse> | null = null
  private cachedResponse: AdaRegistrationResponse | null = null
  private callbacks: AdaRegistrationCallback[] = []
  private stepStatusCallbacks: StepStatusCallback[] = []
  private stepConfigsCallbacks: StepConfigsCallback[] = []
  private currentStepStatuses: Record<string, string> = {}
  private currentStepConfigs: AdaStepConfig[] = []
  private isLoading = false
  private cancelPolling = false

  generateSessionId(): string {
    this.sessionId = uuidv4()
    return this.sessionId
  }

  getSessionId(): string | null {
    return this.sessionId
  }

  getCachedResponse(): AdaRegistrationResponse | null {
    return this.cachedResponse
  }

  isRequestPending(): boolean {
    return this.isLoading
  }

  subscribe(callback: AdaRegistrationCallback): () => void {
    this.callbacks.push(callback)

    if (this.cachedResponse) {
      callback(this.cachedResponse, null)
    }

    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback)
    }
  }

  subscribeToStepStatus(callback: StepStatusCallback): () => void {
    this.stepStatusCallbacks.push(callback)

    if (Object.keys(this.currentStepStatuses).length > 0) {
      callback(this.currentStepStatuses)
    }

    return () => {
      this.stepStatusCallbacks = this.stepStatusCallbacks.filter((cb) => cb !== callback)
    }
  }

  getCurrentStepStatuses(): Record<string, string> {
    return { ...this.currentStepStatuses }
  }

  subscribeToStepConfigs(callback: StepConfigsCallback): () => void {
    this.stepConfigsCallbacks.push(callback)

    if (this.currentStepConfigs.length > 0) {
      callback(this.currentStepConfigs)
    }

    return () => {
      this.stepConfigsCallbacks = this.stepConfigsCallbacks.filter((cb) => cb !== callback)
    }
  }

  getStepConfigs(): AdaStepConfig[] {
    return [...this.currentStepConfigs]
  }

  getStepKeys(): string[] {
    if (this.currentStepConfigs.length > 0) {
      return this.currentStepConfigs.map((step) => step.key)
    }
    return [...DEFAULT_STEP_KEYS]
  }

  private notifyStepConfigsCallbacks(stepConfigs: AdaStepConfig[]) {
    this.currentStepConfigs = [...stepConfigs]
    this.stepConfigsCallbacks.forEach((callback) => {
      try {
        callback(stepConfigs)
      } catch (e) {
        console.error('[AdaRegistrationService] Step configs callback error:', e)
      }
    })
  }

  private notifyCallbacks(response: AdaRegistrationResponse | null, error: Error | null) {
    this.callbacks.forEach((callback) => {
      try {
        callback(response, error)
      } catch (e) {
        console.error('[AdaRegistrationService] Callback error:', e)
      }
    })
  }

  private notifyStepStatusCallbacks(stepStatuses: Record<string, string>) {
    this.currentStepStatuses = { ...stepStatuses }
    this.stepStatusCallbacks.forEach((callback) => {
      try {
        callback(stepStatuses)
      } catch (e) {
        console.error('[AdaRegistrationService] Step status callback error:', e)
      }
    })
  }

  private isStillProcessing(data: any): data is AsyncPollingInProgressResponse {
    return data && data.status === 'processing' && typeof data.result_url === 'string'
  }

  private async pollForResult<T = AdaRegistrationResponse>(resultUrl: string, operationName: string): Promise<T> {
    // Poll indefinitely until cancelled or result received
    // Users can skip via UI after 10 seconds, backend handles persistence
    // Note: Backend has a 3-minute timeout and will fail the request if AI processing exceeds this
    while (true) {
      if (this.cancelPolling) {
        throw new Error(`${operationName} was cancelled`)
      }

      await new Promise((resolve) => setTimeout(resolve, 2000))

      if (this.cancelPolling) {
        throw new Error(`${operationName} was cancelled`)
      }

      try {
        const urlPath = resultUrl.replace(`${window.API_ROOT}/api/`, '')
        const response = await restClientInstance('CUSTOM_GET', 'custom', { url: urlPath })
        const pollData = response.data

        if (this.isStillProcessing(pollData)) {
          continue
        }

        if (pollData.success === false) {
          throw new Error(pollData.message || pollData.error || `${operationName} failed`)
        }

        return pollData as T
      } catch (error: any) {
        // On HTTP errors, continue polling (restClient throws on non-2xx)
        if (error?.status) {
          continue
        }
        throw error
      }
    }
  }

  async initializeSteps(request: AdaRegistrationRequest): Promise<AdaInitializeResponse> {
    const stepKeys = this.getStepKeys()
    const initialStatuses: Record<string, string> = {}
    stepKeys.forEach((key) => {
      initialStatuses[key] = 'pending'
    })
    this.notifyStepStatusCallbacks(initialStatuses)

    const response = await restClientInstance('CUSTOM_POST', 'custom', {
      url: 'ada/registration/steps/initialize/',
      data: {
        ...request,
        key: 'steps',
      },
    })

    const data = response.data

    if (this.isStillProcessing(data)) {
      const result = await this.pollForResult<AdaInitializeResponse>(data.result_url, 'Steps initialization')
      if (result.steps && result.steps.length > 0) {
        this.notifyStepConfigsCallbacks(result.steps)
      }
      return result
    }

    if (data.steps && data.steps.length > 0) {
      this.notifyStepConfigsCallbacks(data.steps)
    }

    return data as AdaInitializeResponse
  }

  async runAllSteps(request: AdaRegistrationRequest): Promise<AdaRunAllStepsResponse> {
    const response = await restClientInstance('CUSTOM_POST', 'custom', {
      url: 'ada/registration/steps/run-all/',
      data: {
        ...request,
        key: 'run_all',
        data_source: 'website',
        data_processor: 'ai',
      },
    })

    const data = response.data

    if (this.isStillProcessing(data)) {
      return this.pollForResult<AdaRunAllStepsResponse>(data.result_url, 'Run all steps')
    }

    return data as AdaRunAllStepsResponse
  }

  async fetchStepResult(resultUrl: string, stepKey: string): Promise<AdaStepResult | null> {
    try {
      // Extract the path from the full URL for restClient
      const urlPath = resultUrl.replace(`${window.API_ROOT}/api/`, '')
      const response = await restClientInstance('CUSTOM_GET', 'custom', { url: urlPath })
      const data = response.data

      return {
        key: stepKey,
        status: data.status || 'success',
        data: data,
        errors: data.errors,
        warnings: data.warnings,
      }
    } catch (error) {
      console.error(`[AdaRegistrationService] Error fetching step ${stepKey}:`, error)
      return null
    }
  }

  async fetchAllStepResults(resultUrls: Record<string, string>): Promise<AdaStepResult[]> {
    const results: AdaStepResult[] = []

    for (const [stepKey, url] of Object.entries(resultUrls)) {
      const result = await this.fetchStepResult(url, stepKey)
      if (result) {
        results.push(result)
      }
    }

    return results
  }

  private async pollStepResultsProgressively(
    sessionId: string,
    onStepComplete: (stepKey: string, status: string) => void
  ): Promise<void> {
    const completedSteps = new Set<string>()
    const failedSteps = new Set<string>()
    const stepKeys = this.getStepKeys()
    const errorCounts = new Map<string, number>()
    const MAX_CONSECUTIVE_ERRORS = 5

    // Poll indefinitely until all steps complete or polling is cancelled
    // Note: Backend has a 3-minute timeout - steps exceeding this will be marked as TIMEOUT errors
    while (completedSteps.size + failedSteps.size < stepKeys.length) {
      if (this.cancelPolling) {
        return
      }

      const pendingSteps = stepKeys.filter((key) => !completedSteps.has(key) && !failedSteps.has(key))

      const pollPromises = pendingSteps.map(async (stepKey) => {
        if (this.cancelPolling) return

        try {
          const response = await restClientInstance('CUSTOM_GET', 'custom', {
            url: `ada/registration/step/result/${sessionId}/${stepKey}/`,
          })
          const data = response.data

          if (data.status === 'processing') {
            errorCounts.delete(stepKey)
            return
          }
          const status = data.status || 'success'
          completedSteps.add(stepKey)
          errorCounts.delete(stepKey)
          onStepComplete(stepKey, status)
        } catch (error: any) {
          if (error?.status === 400) {
            const errorCode = error?.body?.errors?.[0]?.code
            if (errorCode === 'INVALID_KEY' || errorCode === 'INVALID_DATA') {
              failedSteps.add(stepKey)
              errorCounts.delete(stepKey)
              onStepComplete(stepKey, 'failed')
              return
            }
          }

          const currentErrors = errorCounts.get(stepKey) || 0
          errorCounts.set(stepKey, currentErrors + 1)

          if (currentErrors + 1 >= MAX_CONSECUTIVE_ERRORS) {
            console.error(
              `[AdaRegistrationService] Step ${stepKey} failed after ${MAX_CONSECUTIVE_ERRORS} attempts`,
              error
            )
            failedSteps.add(stepKey)
            errorCounts.delete(stepKey)
            onStepComplete(stepKey, 'failed')
          }
        }
      })

      await Promise.all(pollPromises)

      if (completedSteps.size + failedSteps.size < stepKeys.length && !this.cancelPolling) {
        await new Promise((resolve) => setTimeout(resolve, 2000))
      }
    }
  }

  async startBackgroundFetch(request: Omit<AdaRegistrationRequest, 'session_id'>): Promise<void> {
    if (this.isLoading) {
      return
    }

    this.isLoading = true
    this.cachedResponse = null
    this.cancelPolling = false

    const sessionId = this.sessionId || this.generateSessionId()
    const fullRequest: AdaRegistrationRequest = {
      ...request,
      session_id: sessionId,
    }

    this.pendingRequest = (async () => {
      try {
        await this.initializeSteps(fullRequest)

        if (this.cancelPolling) {
          throw new Error('Registration was cancelled')
        }

        const runAllPromise = this.runAllSteps(fullRequest)

        this.pollStepResultsProgressively(sessionId, (stepKey: string, status: string) => {
          if (!this.cancelPolling) {
            this.currentStepStatuses[stepKey] = status
            this.notifyStepStatusCallbacks({ ...this.currentStepStatuses })
          }
        })

        const runAllResponse = await runAllPromise

        if (this.cancelPolling) {
          throw new Error('Registration was cancelled')
        }

        this.cancelPolling = true

        this.notifyStepStatusCallbacks(runAllResponse.step_statuses)

        const stepResults = await this.fetchAllStepResults(runAllResponse.result_urls)

        const combinedResponse: AdaRegistrationResponse = {
          status: runAllResponse.status,
          session_id: runAllResponse.session_id,
          name: request.name,
          company_email: request.company_email,
          country_iso2: request.country_iso2,
          company_website: request.company_website,
          step_statuses: runAllResponse.step_statuses,
          result_urls: runAllResponse.result_urls,
          data: stepResults,
          errors: runAllResponse.errors || [],
          warnings: [],
        }

        this.cachedResponse = combinedResponse
        this.isLoading = false

        this.notifyCallbacks(combinedResponse, null)
        return combinedResponse
      } catch (error) {
        this.isLoading = false
        if (!this.cancelPolling) {
          console.error('[AdaRegistrationService] Background fetch failed:', error)
          this.notifyCallbacks(null, error as Error)
        }
        throw error
      }
    })()
  }

  async waitForResults(): Promise<AdaRegistrationResponse | null> {
    if (this.cachedResponse) {
      return this.cachedResponse
    }

    if (this.pendingRequest) {
      try {
        return await this.pendingRequest
      } catch {
        return null
      }
    }

    return null
  }

  /**
   * Resets the service state. Call this when starting a new registration
   * to clear any stale data from previous sessions.
   *
   * @param preserveSessionId - If true, keeps the existing session_id for KPI tracking continuity.
   *                            Use this when the session was initialized on page load for BI tracking.
   */
  reset(preserveSessionId = false) {
    this.cancelPolling = true
    if (!preserveSessionId) {
      this.sessionId = null
    }
    this.pendingRequest = null
    this.cachedResponse = null
    this.callbacks = []
    this.stepStatusCallbacks = []
    this.stepConfigsCallbacks = []
    this.currentStepStatuses = {}
    this.currentStepConfigs = []
    this.isLoading = false
  }

  /**
   * Set the session ID (used when restoring from persisted state)
   */
  setSessionId(sessionId: string) {
    this.sessionId = sessionId
  }

  /**
   * Fetch all step results from the backend by session ID.
   * This is used when the cached response is lost (e.g., page refresh)
   * but we have a persisted session ID.
   */
  async fetchResultsBySessionId(sessionId: string): Promise<AdaRegistrationResponse | null> {
    try {
      const stepResults: AdaStepResult[] = []
      const stepStatuses: Record<string, string> = {}
      const stepKeys = this.getStepKeys()

      const fetchPromises = stepKeys.map(async (stepKey) => {
        try {
          const response = await restClientInstance('CUSTOM_GET', 'custom', {
            url: `ada/registration/step/result/${sessionId}/${stepKey}/`,
          })
          const data = response.data

          if (data.status === 'processing') {
            console.warn(`[AdaRegistrationService] Step ${stepKey} still processing during session restore`)
            return null
          }

          if (data.status === 'error' || data.errors) {
            console.warn(`[AdaRegistrationService] Step ${stepKey} has errors:`, data.errors || data.status)
            return null
          }

          const hasContent = Object.keys(data).some(
            (key) => !['status', 'errors', 'step_key'].includes(key) && data[key] !== null
          )

          if (hasContent) {
            return {
              key: stepKey,
              status: 'completed',
              data: data,
            } as AdaStepResult
          }

          console.warn(`[AdaRegistrationService] Step ${stepKey} returned empty content`)
          return null
        } catch (error) {
          console.error(
            `[AdaRegistrationService] Error fetching step ${stepKey}:`,
            error instanceof Error ? error.message : error
          )
          return null
        }
      })

      const results = await Promise.all(fetchPromises)

      results.forEach((result, index) => {
        const stepKey = stepKeys[index]
        if (result) {
          stepResults.push(result)
          stepStatuses[stepKey] = 'completed'
        } else {
          stepStatuses[stepKey] = 'failed'
        }
      })

      if (stepResults.length === 0) {
        return null
      }

      const combinedResponse: AdaRegistrationResponse = {
        status: 'completed',
        session_id: sessionId,
        step_statuses: stepStatuses,
        result_urls: {},
        data: stepResults,
        errors: [],
        warnings: [],
      }

      this.cachedResponse = combinedResponse
      this.sessionId = sessionId

      return combinedResponse
    } catch (error) {
      console.error('[AdaRegistrationService] Error fetching results by session ID:', error)
      return null
    }
  }

  /**
   * Update the AdaRegistrationLog with user modifications for KPI tracking.
   * This tracks which AI-suggested fields were accepted vs modified by the user.
   */
  async updateKpiLog(
    aiValues: Record<string, any>,
    userValues: Record<string, any>,
    orgId?: number | null
  ): Promise<void> {
    const sessionId = this.sessionId
    if (!sessionId) {
      return
    }

    const fields: Record<string, { ai_value: any; user_value: any }> = {}

    const allFields = new Set([...Object.keys(aiValues), ...Object.keys(userValues)])

    allFields.forEach((fieldName) => {
      if (fieldName.startsWith('_') || fieldName === 'logo' || fieldName === 'logo_url') return

      fields[fieldName] = {
        ai_value: aiValues[fieldName] ?? null,
        user_value: userValues[fieldName] ?? null,
      }
    })

    if (Object.keys(fields).length === 0) {
      return
    }

    try {
      const payload: Record<string, any> = {
        session_id: sessionId,
        fields,
        data_processor: 'ai',
        data_source: 'website',
      }

      if (orgId) {
        payload.org = orgId
      }

      await restClientInstance('CUSTOM_PUT', 'custom', {
        url: 'ada/registration/kpi/',
        data: payload,
      })
    } catch (error) {
      console.warn(
        '[AdaRegistrationService] KPI update failed (non-critical):',
        error instanceof Error ? error.message : error
      )
    }
  }

  /**
   * Link an organization to the current Ada registration session.
   * This should be called after registration completes to associate the newly
   * created org with the existing session, enabling pending_review to work
   * correctly across logout/login.
   */
  async linkOrgToSession(orgId: number): Promise<void> {
    const sessionId = this.sessionId
    if (!sessionId) {
      return
    }

    try {
      await restClientInstance('CUSTOM_POST', 'custom', {
        url: 'ada/registration/link-org/',
        data: {
          session_id: sessionId,
          org_id: orgId,
        },
      })
    } catch (error) {
      console.warn(
        '[AdaRegistrationService] Failed to link org to session (non-critical):',
        error instanceof Error ? error.message : error
      )
    }
  }
}

export const adaRegistrationService = new AdaRegistrationService()
